import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { putAttractionOffersOrder } from '~/services/AttractionsService';

import { setCacheBuster } from './attractionCacheBuster';
import { attractionQueryKeys } from './attractionQueryKeys';
import { useCanonicalAttractionRedirect } from './useCanonicalAttractionRedirect';

interface PutAttractionOffersOrderData {
  attractionId: string;
  offerIdsInOrder: string[];
}

export function usePutAttractionOffersOrder() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { retargetFromMutationError } = useCanonicalAttractionRedirect();

  return useMutation({
    mutationFn: async ({ attractionId, offerIdsInOrder }: PutAttractionOffersOrderData) => {
      return await putAttractionOffersOrder(attractionId, offerIdsInOrder);
    },
    onSuccess: (_data, { attractionId }) => {
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.offers(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.detail(attractionId) });
      setCacheBuster(attractionId, Date.now().toString());
      enqueueSnackbar('Offer order saved', { variant: 'success' });
    },
    onError: (error: Error, variables) => {
      if (retargetFromMutationError(error, variables.attractionId)) return;
      enqueueSnackbar(`Error saving order: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
